import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { useSettings } from "@/lib/settings";
import { trackGenerateLead } from "@/lib/analytics";
import { Phone } from "lucide-react";

export default function SupportPage() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/messages", form);
      trackGenerateLead("support_form");
      toast.success("Message sent! We'll reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch { toast.error("Failed to send. Try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="support-page">
      <SEO
        title="Contact & Support"
        description="Get in touch with AlikamaFish — questions about your order, classes, subscriptions, or events. We reply within one business day."
        path="/support"
      />
      <section className="bg-[color:var(--color-primary)] text-white py-20">
        <div className="container-x px-6 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mb-4">We're Here to Help</div>
          <h1 className="font-display text-5xl md:text-6xl mb-6">Support</h1>
          <p className="text-white/70 max-w-xl mx-auto mb-8">For immediate assistance, call us. For non-urgent queries, leave us a message below.</p>
          <a href={`tel:${settings.phone}`} className="btn-accent inline-flex text-base" data-testid="support-call-btn">
            <Phone className="w-5 h-5" /> Call {settings.phone}
          </a>
        </div>
      </section>

      <section className="section bg-[color:var(--color-bg)]">
        <div className="container-x max-w-xl">
          <h2 className="font-display text-3xl text-[color:var(--color-primary)] mb-6">Send a Message</h2>
          <form onSubmit={submit} className="space-y-4">
            {[
              { name: "name", label: "Your Name", type: "text" },
              { name: "email", label: "Email", type: "email" },
              { name: "phone", label: "Phone (optional)", type: "tel", required: false },
              { name: "subject", label: "Subject", type: "text" },
            ].map((f) => (
              <div key={f.name}>
                <label className="text-xs uppercase tracking-widest text-gray-600">{f.label}</label>
                <input
                  required={f.required !== false} type={f.type}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2 bg-white"
                  data-testid={`support-input-${f.name}`}
                />
              </div>
            ))}
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-600">Message</label>
              <textarea
                required rows="5"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2 bg-white"
                data-testid="support-input-message"
              />
            </div>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="support-submit-btn">
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
